import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, Sparkles, Bell, BellOff, ArrowUpRight, ArrowRight,
  Loader2, Flame, BarChart3, Eye, Bookmark, CheckCircle2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MyTrendingMaterials = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myTrending, setMyTrending] = useState([]);
  const [allTrends, setAllTrends] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [checkingPush, setCheckingPush] = useState(true);

  useEffect(() => {
    fetchData();
    checkPushStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [trendsRes, notifsRes, boardsRes] = await Promise.all([
        axios.get(`${API}/trends/cached`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/boards`, { withCredentials: true })
      ]);

      setAllTrends(trendsRes.data);
      
      // Filter trend alerts
      const trendAlerts = notifsRes.data.notifications?.filter(
        n => n.notification_type === 'trend_alert'
      ) || [];
      setNotifications(trendAlerts);

      // Find materials in user's boards that are trending
      const userMaterialIds = new Set();
      boardsRes.data.forEach(board => {
        board.items?.forEach(item => {
          if (item.material_id) userMaterialIds.add(item.material_id);
        });
      });

      // Match with trending materials
      const topMaterials = trendsRes.data.top_materials || [];
      const myTrendingMats = topMaterials.filter(m => 
        userMaterialIds.has(m.material_id)
      ).map(m => ({
        ...m,
        rank: topMaterials.findIndex(t => t.material_id === m.material_id) + 1
      }));

      setMyTrending(myTrendingMats);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPushStatus = async () => {
    try {
      const res = await axios.get(`${API}/notifications/push/status`, { withCredentials: true });
      setPushEnabled(res.data.enabled);
    } catch (error) {
      console.error('Failed to check push status:', error);
    } finally {
      setCheckingPush(false);
    }
  };

  const togglePushNotifications = async () => {
    try {
      if (pushEnabled) {
        await axios.delete(`${API}/notifications/push/unsubscribe`, { withCredentials: true });
        setPushEnabled(false);
        toast.success('Push notifications disabilitate');
      } else {
        // Request permission and subscribe
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Get service worker registration
          const registration = await navigator.serviceWorker.ready;
          
          // Subscribe to push
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
            )
          });
          
          // Send to backend
          await axios.post(`${API}/notifications/push/subscribe`, {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          }, { withCredentials: true });
          
          setPushEnabled(true);
          toast.success('Push notifications abilitate!');
        } else {
          toast.error('Permesso push negato dal browser');
        }
      }
    } catch (error) {
      console.error('Push toggle error:', error);
      toast.error('Errore nella configurazione push');
    }
  };

  // Helper function for VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-yellow-500 text-white">#1 Top</Badge>;
    if (rank <= 3) return <Badge className="bg-orange-500 text-white">Top 3</Badge>;
    if (rank <= 10) return <Badge className="bg-emerald-500 text-white">Top 10</Badge>;
    return <Badge variant="secondary">#{rank}</Badge>;
  };

  const getTrendIcon = (score) => {
    if (score > 50) return <Flame className="w-5 h-5 text-orange-500" />;
    if (score > 20) return <TrendingUp className="w-5 h-5 text-emerald-500" />;
    return <ArrowUpRight className="w-4 h-4 text-[var(--gold)]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-[1400px] mx-auto px-6 py-12 pt-28 w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Link to="/trends" className="hover:text-foreground">Trends</Link>
              <span>/</span>
              <span>I Miei Materiali</span>
            </div>
            <h1 className="heading-display text-3xl md:text-4xl font-medium flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-amber-500" />
              I Tuoi Materiali Trending
            </h1>
            <p className="text-muted-foreground mt-2">
              Scopri quali materiali nei tuoi progetti stanno diventando popolari
            </p>
          </div>
          
          {/* Push Notifications Toggle */}
          <div className="flex items-center gap-4">
            <Button
              variant={pushEnabled ? "default" : "outline"}
              onClick={togglePushNotifications}
              disabled={checkingPush}
              data-testid="push-toggle-btn"
            >
              {pushEnabled ? (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Notifiche Attive
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-2" />
                  Attiva Notifiche
                </>
              )}
            </Button>
            <Link to="/trends">
              <Button variant="ghost">
                Vedi Tutti i Trend <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="w-6 h-6 text-amber-600" />
              <span className="text-amber-800 font-medium">Materiali in Trend</span>
            </div>
            <p className="text-4xl font-bold text-amber-900">{myTrending.length}</p>
            <p className="text-sm text-amber-600 mt-1">nei tuoi progetti</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
              <span className="text-emerald-800 font-medium">Miglior Posizione</span>
            </div>
            <p className="text-4xl font-bold text-emerald-900">
              {myTrending.length > 0 ? `#${Math.min(...myTrending.map(m => m.rank))}` : '-'}
            </p>
            <p className="text-sm text-emerald-600 mt-1">nella classifica generale</p>
          </div>
          
          <div className="bg-gradient-to-br from-[var(--gold)]/10 to-[var(--gold)]/5 border border-[var(--gold)]/30 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Bell className="w-6 h-6 text-[var(--gold)]" />
              <span className="text-[var(--gold)] font-medium">Alert Recenti</span>
            </div>
            <p className="text-4xl font-bold text-white">{notifications.length}</p>
            <p className="text-sm text-[var(--gold)] mt-1">notifiche trend</p>
          </div>
        </div>

        {/* My Trending Materials */}
        <div className="mb-12">
          <h2 className="text-xl font-medium mb-6 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Materiali Trending nei Tuoi Progetti
          </h2>
          
          {myTrending.length === 0 ? (
            <div className="text-center py-16 bg-secondary/30 rounded-xl">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-medium mb-2">Nessun materiale trending</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                I materiali nei tuoi board non sono ancora tra i più popolari. 
                Continua ad esplorare e salvare materiali per essere notificato quando diventano trend!
              </p>
              <Link to="/materials">
                <Button>Esplora Materiali</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myTrending.map((material, index) => (
                <Link
                  key={material.material_id}
                  to={`/materials/${material.material_id}`}
                  className="group bg-white border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`trending-material-${material.material_id}`}
                >
                  {/* Material Image */}
                  <div className="aspect-[4/3] bg-secondary relative overflow-hidden">
                    {material.thumbnail ? (
                      <img
                        src={material.thumbnail}
                        alt={material.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl text-muted-foreground">{material.name?.charAt(0)}</span>
                      </div>
                    )}
                    
                    {/* Rank Badge */}
                    <div className="absolute top-3 left-3">
                      {getRankBadge(material.rank)}
                    </div>
                    
                    {/* Trend Indicator */}
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2">
                      {getTrendIcon(material.total_score)}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-medium text-lg group-hover:text-primary transition-colors">
                      {material.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{material.stone_type}</p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <BarChart3 className="w-4 h-4" />
                        <span>Score: {Math.round(material.total_score)}</span>
                      </div>
                      {material.signals && (
                        <>
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            <span>{material.signals.searches || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Bookmark className="w-4 h-4" />
                            <span>{material.signals.board_additions || 0}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Trend Alerts */}
        {notifications.length > 0 && (
          <div>
            <h2 className="text-xl font-medium mb-6 flex items-center gap-2">
              <Bell className="w-5 h-5 text-[var(--gold)]" />
              Alert Recenti
            </h2>
            
            <div className="space-y-3">
              {notifications.slice(0, 5).map(notif => (
                <div
                  key={notif.notification_id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    notif.is_read ? 'bg-white border-border' : 'bg-[var(--gold)]/10 border-[var(--gold)]/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${notif.is_read ? 'bg-secondary' : 'bg-[var(--gold)]/20'}`}>
                      <TrendingUp className={`w-5 h-5 ${notif.is_read ? 'text-muted-foreground' : 'text-[var(--gold)]'}`} />
                    </div>
                    <div>
                      <p className="font-medium">{notif.title?.replace('📈 ', '')}</p>
                      <p className="text-sm text-muted-foreground">{notif.message}</p>
                    </div>
                  </div>
                  {notif.link && (
                    <Link to={notif.link}>
                      <Button variant="ghost" size="sm">
                        Vedi <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
            
            {notifications.length > 5 && (
              <div className="text-center mt-4">
                <Link to="/notifications">
                  <Button variant="outline">
                    Vedi tutte le notifiche ({notifications.length})
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default MyTrendingMaterials;
